import { useRef } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";

import { RISK_CONFIG, RISK_SCALE_ORDER } from "../../constants/riskConfig";
import { ZP } from "../../constants/palette";
import { loop } from "./StepMotif";

/**
 * The drawings in the homepage's Ask · Read · Act cards.
 *
 * Small line loops in the same vocabulary as the Process page's StepMotif —
 * dots, empty rings, hairlines — built on its `loop`, so they share its cycle
 * and every drawing on the site keeps one rhythm:
 *
 *   ask  — an address field fills with typed dots behind a caret, a pin drops
 *          above it, and the field dims, its job done.
 *   read — the five-band scale fills from Very Low up to the answer, and a
 *          tick lands on it. The only colour of the three: the scale is the
 *          reading, and colour on this site means a reading.
 *   act  — dots and empty rings drift down to a filter line. The rings —
 *          what the filter is for — stop at it and fade; clean dots pass.
 *
 * Loops only while on screen; under reduced motion it is drawn once, finished,
 * and holds still. Decorative — the card's copy carries the meaning.
 */
const W = 220;
const H = 120;

/** Read's answer: Moderate, the same band the Score step lands on. */
const REACHED = RISK_SCALE_ORDER.indexOf("0");

/** One looping element, or its still frame when the loop is off. */
const Loop = ({ as: Tag, from, to, opts, moving, reduce, ...attrs }) =>
  moving ? (
    <Tag {...attrs} {...loop(from, to, opts)} />
  ) : (
    <Tag {...attrs} initial={false} animate={reduce ? to : from} />
  );

const Ask = ({ ink, moving, reduce }) => {
  const fy = 66;
  const typed = 9;
  const step = 11;
  return (
    <>
      <Loop
        as={motion.rect}
        x={0.5}
        y={fy}
        width={W - 1}
        height={38}
        rx={6}
        fill="none"
        stroke={ink}
        strokeWidth={1}
        from={{ opacity: 0.6 }}
        to={{ opacity: 0.35 }}
        opts={{ at: 2.6, dur: 0.5 }}
        moving={moving}
        reduce={reduce}
      />
      {Array.from({ length: typed }, (_, i) => (
        <Loop
          key={i}
          as={motion.circle}
          cx={18 + i * step}
          cy={fy + 19}
          r={2}
          fill={ink}
          from={{ opacity: 0 }}
          to={{ opacity: 1 }}
          opts={{ at: 0.5 + i * 0.18, dur: 0.12, out: { opacity: 0 } }}
          moving={moving}
          reduce={reduce}
        />
      ))}
      {/* The caret steps along with the typing and blinks the whole time. */}
      <Loop
        as={motion.g}
        from={{ x: 0 }}
        to={{ x: typed * step }}
        // Linear, to keep pace with the dots appearing at a steady rate; the
        // site curve would run it ahead of them mid-word.
        opts={{ at: 0.5, dur: typed * 0.18, ease: "linear" }}
        moving={moving}
        reduce={reduce}
      >
        <motion.rect
          x={13}
          y={fy + 11}
          width={1.2}
          height={16}
          fill={ink}
          animate={moving ? { opacity: [1, 1, 0, 0] } : { opacity: 1 }}
          transition={{
            duration: 1,
            times: [0, 0.5, 0.5, 1],
            repeat: Infinity,
            repeatDelay: 0.0001,
          }}
        />
      </Loop>
      {/* The pin drops once the address is in. */}
      <Loop
        as={motion.g}
        from={{ y: -14, opacity: 0 }}
        to={{ y: 0, opacity: 1 }}
        opts={{ at: 2.3, dur: 0.5, out: { y: 0, opacity: 0 } }}
        moving={moving}
        reduce={reduce}
      >
        <path
          d="M110 18c-4.4 0-8 3.4-8 7.8 0 5.6 8 13 8 13s8-7.4 8-13c0-4.4-3.6-7.8-8-7.8Z"
          fill="none"
          stroke={ink}
          strokeWidth={1.3}
          strokeLinejoin="round"
        />
        <circle cx={110} cy={25.8} r={2.4} fill={ink} />
      </Loop>
    </>
  );
};

const Read = ({ ink, moving, reduce }) => {
  const bw = (W - 12) / 5;
  const tickX = REACHED * (bw + 3) + bw / 2;
  return (
    <>
      {RISK_SCALE_ORDER.map((score, i) => {
        const band = { x: i * (bw + 3), y: 70, width: bw, height: 8, rx: 2 };
        const unlit = { fill: ink, opacity: 0.12 };
        // Past the answer: never lit, so never animated.
        if (i > REACHED) {
          return <rect key={score} {...band} {...unlit} data-testid="read-band-past" />;
        }
        return (
          <Loop
            key={score}
            as={motion.rect}
            {...band}
            from={unlit}
            to={{ fill: RISK_CONFIG[score].hex, opacity: 1 }}
            opts={{ at: 0.5 + i * 0.35, dur: 0.4 }}
            moving={moving}
            reduce={reduce}
          />
        );
      })}
      <Loop
        as={motion.g}
        from={{ y: -10, opacity: 0 }}
        to={{ y: 0, opacity: 1 }}
        opts={{ at: 0.5 + (REACHED + 1) * 0.35, dur: 0.4 }}
        moving={moving}
        reduce={reduce}
      >
        <rect x={tickX - 0.6} y={44} width={1.2} height={20} fill={ink} />
        <circle cx={tickX} cy={43} r={3} fill={ink} />
      </Loop>
    </>
  );
};

/** Seven drifting things; the two rings are what the filter catches. */
const DRIFT = [
  { x: 20, y: 18 },
  { x: 50, y: 34, caught: true },
  { x: 80, y: 10 },
  { x: 110, y: 28 },
  { x: 140, y: 40, caught: true },
  { x: 170, y: 14 },
  { x: 200, y: 30 },
];

const Act = ({ ink, moving, reduce }) => {
  const line = 64;
  return (
    <>
      <line
        x1={0}
        x2={W}
        y1={line}
        y2={line}
        stroke={ink}
        strokeWidth={1.4}
        strokeDasharray="6 4"
      />
      {DRIFT.map((d, k) => {
        const at = 0.2 + k * 0.15;
        return d.caught ? (
          <Loop
            key={k}
            as={motion.circle}
            cx={d.x}
            r={4}
            fill="none"
            stroke={ink}
            strokeWidth={1}
            from={{ cy: d.y - 24, opacity: 0 }}
            to={{ cy: line - 8, opacity: 0.45 }}
            opts={{ at, dur: 1.6, out: { cy: line - 8, opacity: 0 } }}
            moving={moving}
            reduce={reduce}
          />
        ) : (
          <Loop
            key={k}
            as={motion.circle}
            cx={d.x}
            r={2}
            fill={ink}
            from={{ cy: d.y - 24, opacity: 0 }}
            to={{ cy: line + 18 + (k % 2) * 16, opacity: 1 }}
            opts={{ at, dur: 1.9, out: { cy: line + 30 + (k % 2) * 16, opacity: 0 } }}
            moving={moving}
            reduce={reduce}
          />
        );
      })}
    </>
  );
};

const DRAWINGS = { ask: Ask, read: Read, act: Act };

export default function CardMotif({ kind, ink = ZP.ink }) {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.4 });
  const reduce = useReducedMotion();
  const Drawing = DRAWINGS[kind];

  return (
    <svg
      ref={ref}
      viewBox={`0 0 ${W} ${H}`}
      className="h-auto w-full max-w-[220px]"
      aria-hidden="true"
      focusable="false"
      data-testid={`card-motif-${kind}`}
    >
      <Drawing ink={ink} moving={inView && !reduce} reduce={reduce} />
    </svg>
  );
}
